/**
 * AuthGate — Global Bootstrap Gate
 * 
 * Single source of truth for auth hydration. Wraps ALL routes in the app.
 * Prevents ANY UI from rendering until Supabase session is fully resolved.
 * 
 * Architecture rules:
 * - No route renders before auth hydration completes
 * - No "flash" of public landing for authenticated users
 * - No "flash" of protected content for guests
 * - Deterministic: same input → same output, regardless of timing
 */

import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";

const mbLearnLogo = '/mblearn-logo-HD.png';

/**
 * PERF (P0): gating the WHOLE app on `getSession()` meant every cold start
 * waited for Supabase (token refresh over the network on mobile = seconds of
 * splash) before painting anything. The gate only exists to avoid a flash of
 * the public landing/login for an already-authenticated user, so we now gate
 * ONLY those two routes, and only when a persisted session token actually
 * exists. Protected routes keep their own loading state in ProtectedRoute.
 */
const FLASH_SENSITIVE = new Set(["/", "/auth/login"]);

const hasStoredSession = (): boolean => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) return true;
    }
  } catch {
    return false;
  }
  return false;
};

export const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { pathname } = useLocation();

  const mustGate =
    loading && FLASH_SENSITIVE.has(pathname) && hasStoredSession();

  if (mustGate) {
    return (
      <div className="min-h-[100svh] bg-background flex items-center justify-center">
        <div className="text-center">
          <img
            src={mbLearnLogo}
            alt="MBLearn"
            className="w-[100px] h-auto mx-auto mb-4 animate-pulse"
          />
        </div>
      </div>
    );
  }

  // Auth is resolved (or irrelevant for this route) — render the app
  return <>{children}</>;
};

